import { AuthProvider } from '@refinedev/core';
import { message } from 'antd';
import { AxiosInstance } from 'axios';
import queryString from 'query-string';

import { AUTH_URLS } from './constants/urls';
import User from './model/General/User';
import { captureHandledError } from './sentry.config';

export const authProvider = (axiosInstance: AxiosInstance) => {
  const authProviderConfig: AuthProvider = {
    login: async ({ username, password }) => {
      const params = queryString.stringify(
        { password: password, username: username },
        {
          skipNull: true,
        },
      );
      const config = {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8',
        },
      };

      try {
        await axiosInstance.post(AUTH_URLS.LOGIN_SESSION_ACRONEX, params, config);

        localStorage.setItem('userLogged', 'true');

        return {
          success: true,
          redirectTo: '',
        };
      } catch (error) {
        captureHandledError(error, {
          feature: 'auth',
          action: 'login',
          level: 'warning',
        });

        return {
          success: false,
          error: {
            message: 'Error al iniciar sesión',
            name: 'Email o contraseña invalida',
          },
        };
      }
    },
    check: async () => {
      try {
        const resp = await axiosInstance.get(AUTH_URLS.CHECK_USER_AUTH);
        const authenticated = resp.status === 200 && resp.data.authenticated;

        if (authenticated) {
          if (localStorage.getItem('userLogged') !== 'true') {
            localStorage.setItem('userLogged', 'true');
          }
        }

        return authenticated
          ? { authenticated: true }
          : {
              authenticated: false,
              redirectTo: '/login',
              error: { message: 'No se puedo verificar el usuario', name: 'Not authenticated' },
            };
      } catch (error) {
        const status =
          (error as { statusCode?: number; response?: { status?: number } })?.statusCode ??
          (error as { response?: { status?: number } })?.response?.status;
        const isNetworkError = (error as { code?: string })?.code === 'ERR_NETWORK';
        const isServerError = typeof status === 'number' && status >= 500 && status < 600;

        if (isNetworkError || isServerError) {
          // No forzar logout si hay problemas de red o errores 5xx del servidor
          captureHandledError(error, {
            feature: 'auth',
            action: 'check-auth-network-or-server-error',
            level: 'error',
            tags: {
              status,
              code: (error as { code?: string })?.code,
            },
          });

          console.warn('[authProvider.check] Saltando logout por error de red/servidor', {
            status,
            code: (error as { code?: string })?.code,
          });

          return {
            authenticated: true,
            error: { message: 'Error en el servidor', statusCode: status || 500 },
          };
        }

        captureHandledError(error, {
          feature: 'auth',
          action: 'check-auth-not-authenticated',
          level: 'warning',
          tags: {
            status,
          },
        });

        return {
          authenticated: false,
          redirectTo: '/login',
          error: { message: 'No se puedo verificar el usuario', name: 'Not authenticated' },
        };
      }
    },
    logout: async () => {
      try {
        await axiosInstance.post(AUTH_URLS.LOGOUT, {});

        localStorage.setItem('userLogged', 'false');

        return {
          success: true,
          redirectTo: '/login',
        };
      } catch (error) {
        captureHandledError(error, {
          feature: 'auth',
          action: 'logout',
          level: 'warning',
        });

        return {
          success: false,
          error: {
            message: 'Logout Error',
            name: 'Invalid email or password',
          },
        };
      }
    },
    onError: async error => {
      if (error.status === 401 || error.status === 403) {
        return {
          logout: true,
          redirectTo: '/login',
          error,
        };
      }

      return {};
    },
    getPermissions: async () => ['admin'],
    getIdentity: async () => {
      try {
        const logged = localStorage.getItem('userLogged') === 'true';

        if (!logged) {
          return {
            data: null,
          };
        }

        const userContext = await axiosInstance.get(AUTH_URLS.USER_CONTEXT);

        if (userContext.status !== 200) {
          message.error('Error getting user context');

          return {
            data: null,
          };
        } else {
          const user: User = new User(userContext.data);

          return {
            ...user,
          };
        }
      } catch (error) {
        captureHandledError(error, {
          feature: 'auth',
          action: 'get-identity',
          level: 'error',
        });

        message.error('Error al obtener datos del usuario');
      }
    },
  };

  return authProviderConfig;
};
